import {
  chequeTieneSalidaRegistrada,
  resolverEstadoChequeTercero,
  type EstadoChequeTercero,
} from './chequeTerceroEstado';

export type ChequeTerceroParteInteresada = {
  nroCliente?: number | null;
  nombreCliente?: string | null;
  nroProveedor?: number | null;
  nombreProveedor?: string | null;
  nroBancoDeposito?: number | null;
  nombreBancoDeposito?: string | null;
  nroCuentaDeposito?: number | null;
};

export type ChequeDestinoInput = ChequeTerceroParteInteresada & {
  fechaSalida?: string | null;
  nroMovCajaSalida?: number | null;
  comprobanteSalida?: string | null;
  senial?: number | null;
  concepto?: string | null;
  conciliado?: number | null;
  rechazado?: number | null;
};

export type VencimientoUrgencia = 'vencido' | 'proximo' | 'normal' | 'none';

export function formatParteInteresadaCompact(
  nro?: number | null,
  nombre?: string | null,
  maxNombre = 28
): { linea: string; titulo: string } {
  const n = nro && nro > 0 ? nro : null;
  const nom = (nombre ?? '').trim();
  if (n && nom) {
    const corto = nom.length > maxNombre ? `${nom.slice(0, maxNombre)}…` : nom;
    return { linea: `${corto} · ${n}`, titulo: `${nom} — ${n}` };
  }
  if (n) return { linea: String(n), titulo: String(n) };
  if (nom) {
    const corto = nom.length > maxNombre ? `${nom.slice(0, maxNombre)}…` : nom;
    return { linea: corto, titulo: nom };
  }
  return { linea: '-', titulo: '-' };
}

export function formatClienteCheque(cheque: ChequeTerceroParteInteresada): string {
  return formatParteInteresadaCompact(cheque.nroCliente, cheque.nombreCliente, 999).titulo;
}

export function formatProveedorCheque(cheque: ChequeTerceroParteInteresada): string {
  return formatParteInteresadaCompact(cheque.nroProveedor, cheque.nombreProveedor, 999).titulo;
}

export function formatCuentaDepositoCheque(cheque: ChequeTerceroParteInteresada): string {
  const banco = (cheque.nombreBancoDeposito ?? '').trim();
  const cuenta = cheque.nroCuentaDeposito;
  if (!banco && (cuenta == null || cuenta <= 0)) return '-';
  if (banco && cuenta != null && cuenta > 0) return `${banco} — Cta. ${cuenta}`;
  if (banco) return banco;
  return `Cta. ${cuenta}`;
}

export function formatDestinoSalidaCheque(cheque: ChequeDestinoInput): string {
  const estado = resolverEstadoChequeTercero(cheque);
  if (estado === 'en_cartera') return 'En cartera';

  const prov = formatProveedorCheque(cheque);
  if (prov !== '-') return prov;

  const dep = formatCuentaDepositoCheque(cheque);
  if (dep !== '-') return dep;

  if (chequeTieneSalidaRegistrada(cheque)) return 'Entregado';
  return '-';
}

export function getVencimientoUrgencia(fechaVencimiento?: string | null): VencimientoUrgencia {
  if (!fechaVencimiento?.trim()) return 'none';
  const venc = new Date(fechaVencimiento);
  if (Number.isNaN(venc.getTime())) return 'none';
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  venc.setHours(0, 0, 0, 0);
  const diffDias = Math.round((venc.getTime() - hoy.getTime()) / 86400000);
  if (diffDias < 0) return 'vencido';
  if (diffDias <= 7) return 'proximo';
  return 'normal';
}

export function colorVencimientoUrgencia(urgencia: VencimientoUrgencia): string | undefined {
  switch (urgencia) {
    case 'vencido':
      return 'error.main';
    case 'proximo':
      return 'warning.dark';
    default:
      return undefined;
  }
}

export function etiquetaEstadoFiltro(estado: string): string {
  switch (estado) {
    case 'en_cartera':
      return 'En cartera';
    case 'entregado':
      return 'Entregado';
    case 'conciliado':
      return 'Conciliado';
    case 'rechazado':
      return 'Rechazado';
    case 'anulado':
      return 'Anulado';
    default:
      return estado;
  }
}

export function calcularRangoVencimientoRapido(tipo: string): { desde: string; hasta: string } | null {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  if (tipo === 'este_mes') {
    const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    const fin = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    return { desde: fmt(inicio), hasta: fmt(fin) };
  }
  if (tipo === 'vencidos') {
    const ayer = new Date(hoy);
    ayer.setDate(ayer.getDate() - 1);
    return { desde: '1900-01-01', hasta: fmt(ayer) };
  }
  if (tipo === 'proximos_7') {
    const fin = new Date(hoy);
    fin.setDate(fin.getDate() + 7);
    return { desde: fmt(hoy), hasta: fmt(fin) };
  }
  return null;
}

export type { EstadoChequeTercero };
